import React, { useEffect, useState, useRef } from "react";
import { Stack, Group } from "@mantine/core";
import { ReactComponent as LinkIcon } from "../../../assets/icons/link.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import CustomCard from "../../../components/Elements/Card/Card";
import SectionTitle from "../../../components/Elements/SectionTitle/SectionTitle";
import clsx from "clsx";

const CARD_WIDTH = 108;
const CARD_GAP = 8;
const MAX_WIDTH = 1700;
const PADDING_HORIZONTAL = 48;

export interface QuickLink {
  icon: JSX.Element;
  webUrl: string;
  appUrl: string;
}

interface QuickLinkProps {
  quickLinks: Array<QuickLink>;
  openQuickLink: (quickLink: QuickLink) => void;
  open: () => void;
}

const QuickLinks: React.FC<QuickLinkProps> = ({
  quickLinks,
  openQuickLink,
  open,
}) => {
  const [linksPerRow, setLinksPerRow] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateLinksPerRow = () => {
    if (containerRef.current) {
      const containerWidth =
        Math.min(containerRef.current.offsetWidth, MAX_WIDTH) -
        PADDING_HORIZONTAL;
      const totalLinkCardWidth = CARD_WIDTH + CARD_GAP;
      const newLinkCardsPerRow = Math.floor(
        containerWidth / totalLinkCardWidth
      );
      setLinksPerRow(newLinkCardsPerRow || 1);
    }
  };

  useEffect(() => {
    calculateLinksPerRow();
    window.addEventListener("resize", calculateLinksPerRow);
    return () => window.removeEventListener("resize", calculateLinksPerRow);
  }, []);

  const emptyCardsCount =
    linksPerRow - (quickLinks.length % linksPerRow || linksPerRow);

  return (
    <Stack
      ref={containerRef}
      className="!p-6 !w-full !max-w-[110rem]"
      gap="0.5rem"
    >
      <SectionTitle Icon={LinkIcon} title="Quick Links" />
      <Group className="flex flex-wrap gap-2">
        {quickLinks.map((quickLink, index) => (
          <CustomCard
            key={index}
            className="!w-[101px] !h-[101px] !justify-center !items-center card-hover"
            onClick={() => openQuickLink(quickLink)}
          >
            {quickLink.icon}
          </CustomCard>
        ))}
        {Array.from({ length: emptyCardsCount }).map((_, index) => (
          <CustomCard
            key={`empty-${index}`}
            withBorder
            className={clsx(
              "!w-[101px] !h-[101px] !justify-center !items-center card-hover ",
              index != 0 ? "!border-dashed pointer-events-none" : ""
            )}
            onClick={open}
          >
            <PlusIcon className={index != 0 ? "!text-[#28282d]" : ""} />
          </CustomCard>
        ))}
      </Group>
    </Stack>
  );
};

export default QuickLinks;
